import { useState } from "react";
import { useRootStore } from "store";
import { usePermission } from "hooks/use-permissions";

import { Link } from "react-router";
import { useGlobalContext } from "app/store/GlobalContext";
import {
  Settings24Regular,
  ChatHelp24Regular,
  PersonSupport24Regular,
  SignOut24Regular,
  ChevronDown20Regular,
  ChevronUp20Regular
} from "@fluentui/react-icons";

import "../MainMenuFullscreen.scss";

type Props = {
  onCloseMenu: () => void;
  onLogout: () => void;
};

export default function UserProfileDropdown({ onCloseMenu, onLogout }: Props) {
  const { handleNavigation } = useGlobalContext();
  const { user } = useRootStore((state) => state);
  const { staticInformation } = useRootStore((state) => state.global);
  const isInternalUser = usePermission("isInternal");

  const [showUserMenu, setShowUserMenu] = useState(false);

  const givenName = user.userInfo?.givenName || "";
  const surName = user.userInfo?.surName || "";
  const email = user.userInfo?.email || "";

  // Generate initials from first and last name
  const initials = `${givenName.charAt(0)}${surName.charAt(0)}`.toUpperCase();

  const renderSettingsMenuItem = () => {
    return (
      <Link
        to="/settings"
        onClick={(e) => {
          handleNavigation({
            url: "/settings",
            eventTitle: "Mina inställningar",
            e: e
          });
          onCloseMenu();
        }}
        className="fullscreenMenuLink fullscreenMenuLinkWithIcon fullscreenMenuLink--small"
      >
        <Settings24Regular className="fullscreenMenuIcon" />
        <span>Mina inställningar</span>
      </Link>
    );
  };

  const renderHelpMenuItem = () => {
    return (
      <a
        href={staticInformation.supportContactInformation.externalSupportSite}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onCloseMenu}
        className="fullscreenMenuLink fullscreenMenuLinkWithIcon fullscreenMenuLink--small"
      >
        <ChatHelp24Regular className="fullscreenMenuIcon" />
        <span>Hjälp</span>
      </a>
    );
  };

  const renderCustomerSupportItems = () => {
    return (
      <a
        href={staticInformation.supportContactInformation.internalSupportSite}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onCloseMenu}
        className="fullscreenMenuLink fullscreenMenuLinkWithIcon fullscreenMenuLink--small"
      >
        <PersonSupport24Regular className="fullscreenMenuIcon" />
        <span>Support</span>
      </a>
    );
  };

  return (
    <div className="fullscreenMenuSection fullscreenMenuUserSection">
      {/* User profile display */}
      <div className="fullscreenMenuUserProfile">
        <div className="fullscreenMenuUserAvatar">
          <span>{initials}</span>
        </div>
        <div className="fullscreenMenuUserInfo">
          <div className="fullscreenMenuUserName">
            {givenName} {surName}
          </div>
          <div className="fullscreenMenuUserEmail">{email}</div>
        </div>
        {/* Toggle button */}
        <button
          className="dropdownToggle"
          onClick={() => setShowUserMenu((prev) => !prev)}
          aria-expanded={showUserMenu}
          aria-label="Toggle user menu"
        >
          {showUserMenu ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </button>
      </div>

      {showUserMenu && (
        <>
          {renderSettingsMenuItem()}
          {/* Help link for all users */}
          {staticInformation?.supportContactInformation?.externalSupportSite &&
            renderHelpMenuItem()}

          {/* Customer support for internal users */}
          {isInternalUser &&
            staticInformation?.supportContactInformation?.internalSupportSite &&
            renderCustomerSupportItems()}

          {/* Logout button */}
          <button
            onClick={() => {
              onCloseMenu();
              onLogout();
            }}
            className="fullscreenMenuLink fullscreenMenuLinkWithIcon fullscreenMenuLogout fullscreenMenuLink--small"
          >
            <SignOut24Regular className="fullscreenMenuIcon" />
            <span>Logga ut</span>
          </button>
        </>
      )}
    </div>
  );
}
